import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AcsCard, AcsLogo, AcsPageShell } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api } from '../api/client';

/**
 * Registro delle attività amministrative e di sicurezza (§6.9).
 *
 * Mostra chi ha fatto cosa e quando. Non contiene il contenuto dei form: è
 * tracciabilità delle operazioni, non una seconda copia dei dati dei visitatori.
 * L'intestazione lo dice esplicitamente, perché è una domanda che chiunque
 * apra questa schermata si pone.
 */

/** Etichette leggibili delle azioni: i codici interni restano nel database. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  'auth.login.succeeded': 'Accesso riuscito',
  'auth.login.failed': 'Accesso fallito',
  'auth.logout': 'Uscita',
  'auth.sessions.revoked': 'Sessioni revocate',
  'event.created': 'Fiera creata',
  'event.updated': 'Fiera modificata',
  'event.policy.changed': 'Informativa attiva cambiata',
  'device.paired': 'Tablet registrato',
  'device.token_reissued': 'Codice tablet rigenerato',
  'device.revoked': 'Tablet revocato',
  'policy.created': 'Bozza creata',
  'policy.draft_updated': 'Bozza modificata',
  'policy.published': 'Testo pubblicato',
  'consent.withdrawn': 'Consenso revocato',
  'consent.admin_correction': 'Correzione amministrativa del consenso',
  'lead.rectified': 'Contatto rettificato',
  'lead.erased': 'Contatto cancellato',
  'lead.anonymized': 'Contatto anonimizzato',
  'export.generated': 'Export generato',
  'user.created': 'Utente creato',
  'user.deactivated': 'Utente disattivato',
  'offline.queue.purged': 'Coda offline svuotata',
};

export function AuditPage() {
  const { t } = useI18n();
  const [action, setAction] = useState('');

  const audit = useQuery({
    queryKey: ['audit', action],
    queryFn: () => api.listAudit(action || undefined),
  });

  const events = audit.data?.events ?? [];
  const availableActions = [...new Set(events.map((event) => event.action))].sort();

  return (
    <AcsPageShell header={<AcsLogo />}>
      <Link to="/app" style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>
      <h1>{t.audit.title}</h1>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.audit.intro}</p>

      <label className="acs-field" style={{ maxWidth: '22rem' }}>
        <span className="acs-field__label">{t.audit.filterAction}</span>
        <select
          className="acs-input"
          value={action}
          onChange={(event) => setAction(event.target.value)}
        >
          <option value="">{t.audit.all}</option>
          {/* L'elenco si costruisce da ciò che è realmente accaduto. */}
          {(action ? [action, ...availableActions] : availableActions).map((value) => (
            <option key={value} value={value}>
              {ACTION_LABELS[value] ?? value}
            </option>
          ))}
        </select>
      </label>

      {audit.isLoading ? <p>{t.common.loading}</p> : null}
      {events.length === 0 && !audit.isLoading ? <p>{t.audit.none}</p> : null}

      {events.length > 0 ? (
        <AcsCard>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[t.audit.when, t.audit.what, t.audit.target, t.audit.details].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        textAlign: 'left',
                        padding: 'var(--acs-space-2)',
                        borderBottom: '2px solid var(--acs-color-border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td style={cell}>{new Date(event.occurredAt).toLocaleString()}</td>
                    <td style={cell}>{ACTION_LABELS[event.action] ?? event.action}</td>
                    <td style={cell}>{event.targetType ?? '—'}</td>
                    <td style={{ ...cell, color: 'var(--acs-color-text-muted)' }}>
                      {formatContext(event.context)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AcsCard>
      ) : null}
    </AcsPageShell>
  );
}

const cell = {
  padding: 'var(--acs-space-2)',
  borderBottom: '1px solid var(--acs-color-border)',
  verticalAlign: 'top' as const,
};

/** Il contesto contiene solo scalari tecnici: si può mostrare così com'è. */
function formatContext(context: Record<string, unknown> | null): string {
  if (!context) return '—';
  return Object.entries(context)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}
